import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

interface GettingStartedPageProps {
  themeJsonExample: string
  templateLiquidExample: string
}

const themePackageLayout = `your-theme-key/
├── theme.json
├── demo.json
├── template.liquid
├── preview.png
└── assets/
    ├── style.css
    ├── theme.js  (optional)
    └── demo/
        └── *.webp`

export function GettingStartedPage({ themeJsonExample, templateLiquidExample }: GettingStartedPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>Getting Started: Build Your Theme</TypographyH2>
        <TypographyP>
          A curated theme package is a small set of files on disk. Follow this guide to create your first minimal theme.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>The minimum files a theme needs</TypographyH3>
        <TypographyP>
          Every theme is a folder under <TypographyInlineCode>themes/&lt;key&gt;/</TypographyInlineCode> with this base layout:
        </TypographyP>
        <CodeBlock code={themePackageLayout} className="my-4" />
        <TypographyList>
          <li><TypographyInlineCode>theme.json</TypographyInlineCode> — declares which block instances your theme uses</li>
          <li><TypographyInlineCode>demo.json</TypographyInlineCode> — the example wedding your demo page renders, with the photographs it addresses</li>
          <li><TypographyInlineCode>template.liquid</TypographyInlineCode> — your layout and CSS with block slots</li>
          <li><TypographyInlineCode>assets/style.css</TypographyInlineCode> — the stylesheet for your theme</li>
          <li><TypographyInlineCode>assets/theme.js</TypographyInlineCode> — optional trusted browser runtime</li>
          <li><TypographyInlineCode>assets/demo/</TypographyInlineCode> — the images <TypographyInlineCode>demo.json</TypographyInlineCode> declares; only declared ones resolve</li>
          <li><TypographyInlineCode>preview.png</TypographyInlineCode> — the catalog preview image at the theme root, not inside <TypographyInlineCode>assets/</TypographyInlineCode></li>
        </TypographyList>
        <TypographyP className="mt-4 text-sm text-muted-foreground">
          theme.json, demo.json, template.liquid, assets/style.css, and preview.png are required. Additional files under assets/ are optional, such as images referenced by your CSS or template and a declared runtime entrypoint.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Real example: <TypographyInlineCode>theme.json</TypographyInlineCode></TypographyH3>
        <TypographyP>
          This manifest declares which block instances your theme uses and their types:
        </TypographyP>
        <CodeBlock code={themeJsonExample} language="json" className="my-4" />
        <TypographyP className="text-sm text-muted-foreground">
          Each block gets a unique instance ID (here: <TypographyInlineCode>hero</TypographyInlineCode>, <TypographyInlineCode>story</TypographyInlineCode>, etc.) and a platform-defined type.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Real example: <TypographyInlineCode>template.liquid</TypographyInlineCode></TypographyH3>
        <TypographyP>
          Your HTML layout with block slots. Use the <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode> tag for each block instance:
        </TypographyP>
        <CodeBlock code={templateLiquidExample} language="liquid" className="my-4" />
        <TypographyP className="text-sm text-muted-foreground">
          The theme designer controls layout and styling. The platform controls block rendering, content, and workflows.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Optional JavaScript runtime</TypographyH3>
        <TypographyP>
          Every theme uses contract v3. If the design needs client-side interaction, add an{" "}
          <TypographyInlineCode>assets/theme.js</TypographyInlineCode> entrypoint, and declare it under{" "}
          <TypographyInlineCode>runtime</TypographyInlineCode> in <TypographyInlineCode>theme.json</TypographyInlineCode>.
          Dependencies are optional and must use platform IDs from the allowlisted dependencies reference.
        </TypographyP>
        <TypographyP className="text-sm text-muted-foreground">
          Runtime code is reviewed as trusted application code. Do not place script tags or inline event handlers in{" "}
          <TypographyInlineCode>template.liquid</TypographyInlineCode>.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Next steps</TypographyH3>
        <TypographyList>
          <li>Create a folder at <TypographyInlineCode>themes/your-theme-key/</TypographyInlineCode></li>
          <li>Add <TypographyInlineCode>theme.json</TypographyInlineCode> declaring your block instances</li>
          <li>Add <TypographyInlineCode>template.liquid</TypographyInlineCode> with your layout</li>
          <li>When needed, declare one optional <TypographyInlineCode>assets/theme.js</TypographyInlineCode> runtime</li>
          <li>Read the <TypographyInlineCode>theme.json</TypographyInlineCode> reference for manifest options</li>
          <li>Read the <TypographyInlineCode>template.liquid</TypographyInlineCode> reference for layout syntax</li>
        </TypographyList>
      </div>

      <div>
        <TypographyP className="text-sm text-muted-foreground">
          Themes are versioned, reusable packages. Once published, they appear in the couple's theme picker and can be selected by many invitations.
        </TypographyP>
      </div>
    </div>
  )
}
